import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import JobService from "../../api/JobService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import Modal from "../../components/Modal/Modal";
import { AdjustmentOverview } from "../../components/ServicesOverviewTypes/AdjustmentOverview";
import { InfestationOverview } from "../../components/ServicesOverviewTypes/InfestationOverview";
import { MaintenanceOverview } from "../../components/ServicesOverviewTypes/MaintenanceOverview";
import { MonitorsToBeChecked } from "../../components/ServicesOverviewTypes/MonitorsToBeChecked";
import { NoInfestationOverview } from "../../components/ServicesOverviewTypes/NoInfestationOverview";
import { UnreachableOverview } from "../../components/ServicesOverviewTypes/UnreachableOverview";
import { useDocumentation } from "../../hooks/useDocumentation";
import { useScreenSize } from "../../hooks/useScreenSize";
import { ReturnArrowIcon } from "../../icons/ReturnArrowIcon/ReturnArrowIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { splitDateTime } from "../../services/DateTimeService";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import styles from "../../styles/property-area-services-overview.module.css";
import { Monitor } from "../../types/monitor";
import { calculateTotalTime } from "../../utils/calculateTotalTime";

export const PropertyAreaServicesOverview = () => {
  const { isDesktop } = useScreenSize();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const monitors = location.state.monitor?.monitors;
  const { t } = useTranslation();
  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );
  const [jobId, setJobId] = useState("");
  const [loading, setLoading] = useState(true);
  const [infestationMonitors, setInfestationMonitors] = useState<any[]>([]);
  const [noInfestationMonitors, setNoInfestationMonitors] = useState<any[]>([]);
  const [maintenanceMonitors, setMaintenanceMonitors] = useState<any[]>([]);
  const [unreachableMonitors, setUnreachableMonitors] = useState<any[]>([]);
  const [monitorsToBeChecked, setMonitorsToBeChecked] = useState<any[]>([]);
  const [adjustedMonitors, setAdjustedMonitors] = useState<any[]>([]);
  const [allMonitorsWithVersions, setAllMonitorsWithVersions] = useState<any[]>(
    []
  );
  const [infestationCountMonitors, setInfestationCountMonitors] =
    useState<number>();
  const [noInfestationCountMonitors, setNoInfestationCountMonitors] =
    useState<number>();
  const [maintenanceCountMonitors, setMaintenanceCountMonitors] =
    useState<number>();
  const [unreachableCountMonitors, setUnreachableCountMonitors] =
    useState<number>();
  const [monitorsToBeCheckedCount, setMonitorsToBeCheckedCount] =
    useState<number>();
  const [adjustedMonitorsCount, setAdjustedMonitorsCount] = useState<number>();
  const [lastEventDate, setLastEventDate] = useState<string | null>(null);
  const propertyAreaName = location.state.monitor.propertyArea;
  const propertyAreaId = location.state.monitor.propertyAreaId;

  const isOnline = useSelector((state: RootState) => state.online.isOnline);

  const { fetchDocumentation, requestLoading, noDocument, setNoDocument } =
    useDocumentation();

  const selectedAppointment = useSelector(
    (state: RootState) => state.appointment
  );
  const overviewAppointment = useSelector(
    (state: RootState) => state.overviewAppointment
  );

  const appointmentPropAreas = useSelector(
    (state: RootState) =>
      state.appointmentPropertyAreas.appointmentPropertyAreas
  );

  let appointment = selectedAppointment;
  const readonly = location.state?.readonly;
  if (readonly) {
    appointment = overviewAppointment;
  }

  const propertyAreaLabel =
    appointmentPropAreas.find((area) => area.id === propertyAreaId)?.label ??
    propertyAreaName;

  useEffect(() => {
    if (appointment.jobs.length > 0) {
      const copiedJobs = Array.from(appointment.jobs);
      const lastJobId = copiedJobs.pop();
      setJobId(lastJobId.id);
    }
  }, [appointment.jobs]);

  useEffect(() => {
    const fetchLastEvent = async () => {
      if (!overviewAppointment.id) return;

      const result = await calculateTotalTime(overviewAppointment.id);

      if (result.lastEvent?.createdAt) {
        const date = splitDateTime(result.lastEvent.createdAt).date;
        setLastEventDate(date);
      }
    };

    fetchLastEvent();
  }, [overviewAppointment.id]);

  const filterMonitorsToBeChecked = (
    monitors: any,
    associatedMonitors: any
  ) => {
    return monitors.filter(
      (monitor: any) =>
        !associatedMonitors.some((associated: any) => associated === monitor.id)
    );
  };

  const mapEventsToMonitors = (monitors: any, events: any, eventType: any) => {
    return events
      .filter((event: any) => event.eventType === eventType)
      .map((event: any) => {
        const matchingMonitor = monitors.find(
          (monitor: any) => monitor.id === event.monitorId
        );

        if (matchingMonitor) {
          return {
            eventId: event.id,
            id: matchingMonitor.id,
            monitorType: matchingMonitor.monitorType,
            location: matchingMonitor.location,
            position: matchingMonitor.position,
            isTemporary: matchingMonitor.isTemporary,
            infestation: event.infestation,
          };
        }
        return null;
      })
      .sort((a: any, b: any) => {
        // Sort non-temporary monitors first
        if (!a.isTemporary && b.isTemporary) return -1;
        if (a.isTemporary && !b.isTemporary) return 1;

        const typeCompare = a.monitorType.localeCompare(
          b.monitorType,
          undefined,
          { sensitivity: "base" }
        );
        if (typeCompare !== 0) return typeCompare;

        // Sort by position numerically
        return a.position - b.position;
      })
      .filter(Boolean);
  };

  const getAdjustedMonitors = useCallback(async () => {
    if (appointment.id && propertyAreaId) {
      try {
        setLoading(true);

        const { data } = await AppointmentService.GetMonitorsWithVersions(
          appointment.id,
          propertyAreaId
        );

        setAllMonitorsWithVersions(data);

        const filteredMonitors = data.filter((monitor: any) =>
          monitor.versions.some((version: any) => version.isOriginal === 0)
        );

        setAdjustedMonitors(filteredMonitors || []);
        setAdjustedMonitorsCount(filteredMonitors.length || 0);

        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    }
  }, [appointment.id, propertyAreaId]);

  const groupMonitorsByEventType = useCallback(async () => {
    if (jobId) {
      try {
        setLoading(true);
        const { data } = await JobService.GetGroupedEventsByJob(jobId);

        let allEventMonitors: string[] = [];

        const filteredData = data.filter(
          (item: any) => item.propertyAreaId === propertyAreaId
        );

        filteredData.forEach((areaData: any) => {
          const infestationEvents = areaData.events.infestation || [];
          const maintenanceEvents = areaData.events.maintenancePerformed || [];
          const unreachableEvents = areaData.events.unreachableOrMissing || [];
          const noInfestationEvents = areaData.events.noInfestation || [];

          allEventMonitors = [
            ...allEventMonitors,
            ...infestationEvents.map((event: any) => event.monitorId),
            ...maintenanceEvents.map((event: any) => event.monitorId),
            ...unreachableEvents.map((event: any) => event.monitorId),
            ...noInfestationEvents.map((event: any) => event.monitorId),
          ];

          setInfestationMonitors(
            mapEventsToMonitors(monitors, infestationEvents, "infestation")
          );
          setMaintenanceMonitors(
            mapEventsToMonitors(
              monitors,
              maintenanceEvents,
              "maintenancePerformed"
            )
          );
          setNoInfestationMonitors(
            mapEventsToMonitors(monitors, noInfestationEvents, "noInfestation")
          );
          setUnreachableMonitors(
            mapEventsToMonitors(
              monitors,
              unreachableEvents,
              "unreachableOrMissing"
            )
          );

          setInfestationCountMonitors(infestationEvents.length);
          setNoInfestationCountMonitors(noInfestationEvents.length);
          setMaintenanceCountMonitors(maintenanceEvents.length);
          setUnreachableCountMonitors(unreachableEvents.length);
        });

        const monitorsToBeChecked = filterMonitorsToBeChecked(
          monitors,
          allEventMonitors
        );

        setMonitorsToBeChecked(
          monitorsToBeChecked.sort((a: any, b: any) => {
            // Sort non-temporary monitors first
            if (!a.isTemporary && b.isTemporary) return -1;
            if (a.isTemporary && !b.isTemporary) return 1;

            const typeCompare = a.monitorType.localeCompare(
              b.monitorType,
              undefined,
              { sensitivity: "base" }
            );
            if (typeCompare !== 0) return typeCompare;

            // Sort by position numerically
            return a.position - b.position;
          })
        );
        setMonitorsToBeCheckedCount(monitorsToBeChecked.length);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    }
  }, [dispatch, jobId, propertyAreaName, monitors]);

  useEffect(() => {
    const updateAllMonitors = () => {
      const findOriginalVersion = (monitorId: string) => {
        const monitorWithVersions = allMonitorsWithVersions.find(
          (m) => m.id === monitorId
        );

        if (monitorWithVersions) {
          const originalVersion = monitorWithVersions.versions.find(
            (version: any) => version.isOriginal === 1
          );

          return originalVersion;
        }

        return null;
      };

      // Function to update a monitor if its location and position are different from the original version
      const updateMonitorLocationAndPosition = (monitors: any) => {
        return monitors.map((monitor: Monitor) => {
          const originalVersion = findOriginalVersion(monitor.id);

          if (
            originalVersion &&
            (monitor.location !== originalVersion.location ||
              monitor.position !== originalVersion.position)
          ) {
            return {
              ...monitor,
              location: originalVersion.location,
              position: originalVersion.position,
            };
          }

          return monitor;
        });
      };

      setInfestationMonitors(
        updateMonitorLocationAndPosition(infestationMonitors)
      );
      setNoInfestationMonitors(
        updateMonitorLocationAndPosition(noInfestationMonitors)
      );
      setMaintenanceMonitors(
        updateMonitorLocationAndPosition(maintenanceMonitors)
      );
      setUnreachableMonitors(
        updateMonitorLocationAndPosition(unreachableMonitors)
      );
      setMonitorsToBeChecked(
        updateMonitorLocationAndPosition(monitorsToBeChecked)
      );
    };

    if (allMonitorsWithVersions.length > 0) {
      updateAllMonitors();
    }
  }, [allMonitorsWithVersions]);

  useEffect(() => {
    if (isOnline) {
      getAdjustedMonitors();
      groupMonitorsByEventType();
    }
  }, [isOnline, groupMonitorsByEventType]);

  const handleBackButtonClicked = () => {
    goOneStepBack(navigate);
  };

  return (
    <AppLayout hideHeader={!isCustomerAdmin}>
      <div>
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          showDocumentation={false}
        />

        <div>
          <AppointmentDetails
            isServicesOverview={true}
            lastEventDate={lastEventDate}
          />
        </div>
        {loading && <LoadingSpinner loading={loading} />}

        {!loading && (
          <div className={styles["content-wrapper"]}>
            <ReturnArrowIcon
              handleBackButtonClicked={handleBackButtonClicked}
            />{" "}
            <span className={styles["title"]}>{propertyAreaLabel}</span>
            <div className={styles.monitors}>
              <InfestationOverview
                monitors={infestationMonitors}
                infestationCount={infestationCountMonitors}
                lastEventDate={lastEventDate}
              />

              <UnreachableOverview
                monitors={unreachableMonitors}
                infestationCount={unreachableCountMonitors}
                lastEventDate={lastEventDate}
              />

              <MonitorsToBeChecked
                monitors={monitorsToBeChecked}
                count={monitorsToBeCheckedCount}
              />

              <NoInfestationOverview
                monitors={noInfestationMonitors}
                infestationCount={noInfestationCountMonitors}
                lastEventDate={lastEventDate}
              />

              <MaintenanceOverview
                monitors={maintenanceMonitors}
                infestationCount={maintenanceCountMonitors}
                lastEventDate={lastEventDate}
              />

              <AdjustmentOverview
                monitors={adjustedMonitors}
                adjustmentCount={adjustedMonitorsCount}
              />
            </div>
            {!isDesktop && (
              <FloatingButtons>
                <CustomButton
                  color="gray"
                  icon="leftarrow"
                  iconPosition="left"
                  width={70}
                  onClick={handleBackButtonClicked}
                >
                  {t("Back to overview")}
                </CustomButton>

                {readonly && (
                  <CustomButton
                    color="gray"
                    icon="document"
                    width={48}
                    loading={requestLoading}
                    onClick={() =>
                      fetchDocumentation(appointment.document?.url)
                    }
                  />
                )}
              </FloatingButtons>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={noDocument}
        message={t("NoDocument")}
        confirmText="OK"
        iconType="alert"
        onConfirm={() => setNoDocument(false)}
      />
    </AppLayout>
  );
};
